import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { IdeaUpdate } from '@/lib/types';

/**
 * Hook to fetch the most recent updates for an idea
 */
export function useIdeaUpdates(ideaId: string | null, limit: number = 3) {
  const [updates, setUpdates] = useState<IdeaUpdate[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUpdates = useCallback(async () => {
    if (!ideaId) {
      setUpdates([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('idea_updates')
        .select('*')
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching idea updates:', error);
        setUpdates([]);
      } else {
        setUpdates((data as unknown as IdeaUpdate[]) || []);
      }
    } finally {
      setLoading(false);
    }
  }, [ideaId, limit]);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  return { updates, loading, refetch: fetchUpdates };
}
